import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import {
  skinSeoulProductsClient,
  type CreditBoostUpdateItem,
  type CreditBoostUpdateRequestProduct,
  type CreditBoostUpdateResult,
  type SkinSeoulProductsClient,
} from '@/infrastructure/SkinSeoulProductsClient';
import type { SkinSeoulAdminProduct } from '@/models/ProductTypes';
import { SkinSeoulProductType } from '@/models/ProductTypes';

export interface CreditBoostReadback {
  sku: string;
  creditBoostPercent: number;
  creditBoostRevision?: string;
}

type CreditBoostClient = Pick<SkinSeoulProductsClient, 'updateCreditBoosts' | 'getProductsBySku'>;

interface CreditBoostEditorProps {
  product: SkinSeoulAdminProduct;
  isProductSaving: boolean;
  onConfirmed: (selectionKey: string, values: CreditBoostReadback[]) => void;
  onSavingChange?: (saving: boolean) => void;
  client?: CreditBoostClient;
}

type RowResult =
  | { status: 'failed'; message: string }
  | { status: 'unconfirmed'; message: string }
  | { status: 'confirmed'; message: string };

export const getCreditBoostSelectionKey = (product: SkinSeoulAdminProduct) =>
  `${product.productId ?? ''}:${product.sku}`;

const targetsFor = (product: SkinSeoulAdminProduct): SkinSeoulAdminProduct[] => {
  if (product.type === SkinSeoulProductType.Variable) {
    return (product.variations ?? []).filter((variation) => variation.type === SkinSeoulProductType.Variation);
  }
  return product.type === SkinSeoulProductType.Simple || product.type === SkinSeoulProductType.Variation
    ? [product]
    : [];
};

const initialDraftFor = (product: SkinSeoulAdminProduct): Record<string, string> =>
  Object.fromEntries(targetsFor(product).map((target) => [target.sku, String(target.creditBoostPercent ?? 0)]));

const parseBoost = (value: string): number | null => {
  if (!/^(0|[1-9]\d*)$/.test(value)) {
    return null;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

const flattenProducts = (products: SkinSeoulAdminProduct[]): SkinSeoulAdminProduct[] =>
  products.flatMap((product) => [product, ...(product.variations ?? [])]);

export const mergeCreditBoostReadback = (
  product: SkinSeoulAdminProduct,
  values: CreditBoostReadback[],
): SkinSeoulAdminProduct => {
  const bySku = new Map(values.map((value) => [value.sku, value]));
  const merge = (item: SkinSeoulAdminProduct): SkinSeoulAdminProduct => {
    const value = bySku.get(item.sku);
    return value
      ? {
          ...item,
          creditBoostPercent: value.creditBoostPercent,
          ...(value.creditBoostRevision === undefined ? {} : { creditBoostRevision: value.creditBoostRevision }),
        }
      : item;
  };
  return {
    ...merge(product),
    ...(product.variations ? { variations: product.variations.map(merge) } : {}),
  };
};

export function CreditBoostEditor({
  product,
  isProductSaving,
  onConfirmed,
  onSavingChange,
  client = skinSeoulProductsClient,
}: CreditBoostEditorProps) {
  const selectionKey = getCreditBoostSelectionKey(product);
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;
  const productRef = useRef(product);
  productRef.current = product;
  const targets = useMemo(() => targetsFor(product), [product]);
  const [draft, setDraft] = useState<Record<string, string>>(() => initialDraftFor(product));
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(initialDraftFor(productRef.current));
    setOverrides({});
    setResults({});
  }, [selectionKey]);

  const parsed = new Map(targets.map((target) => [target.sku, parseBoost(draft[target.sku] ?? '')]));
  const changed = targets.filter((target) => parsed.get(target.sku) !== (target.creditBoostPercent ?? 0));
  const hasInvalid = targets.some((target) => parsed.get(target.sku) === null);
  const hasHighWithoutOverride = changed.some((target) => {
    const value = parsed.get(target.sku);
    return typeof value === 'number' && value > 20 && !overrides[target.sku];
  });
  const blocked =
    isProductSaving || isSaving || targets.length === 0 || changed.length === 0 || hasInvalid || hasHighWithoutOverride;

  const submit = async () => {
    if (blocked) {
      return;
    }
    const updates: CreditBoostUpdateItem[] = changed.map((target) => {
      const value = parsed.get(target.sku)!;
      return {
        sku: target.sku,
        creditBoostPercent: value,
        ...(value > 20 ? { creditBoostOverride: true } : {}),
      };
    });
    const request: CreditBoostUpdateRequestProduct[] =
      product.type === SkinSeoulProductType.Variable ? [{ sku: product.sku, variations: updates }] : updates;
    const submittedSelection = selectionKey;
    setIsSaving(true);
    onSavingChange?.(true);
    setResults({});
    try {
      const response = await client.updateCreditBoosts(request);
      const nextResults: Record<string, RowResult> = {};
      const successes: Array<Extract<CreditBoostUpdateResult, { success: true }>> = [];
      for (const result of response) {
        if (result.success) {
          successes.push(result);
        } else {
          nextResults[result.sku] = { status: 'failed', message: result.error };
        }
      }
      if (selectionKeyRef.current !== submittedSelection) {
        return;
      }
      setResults(nextResults);

      if (successes.length === 0) {
        return;
      }
      try {
        const readback = flattenProducts(await client.getProductsBySku(successes.map((result) => result.sku)));
        const confirmed: CreditBoostReadback[] = [];
        for (const result of successes) {
          const saved = readback.find((item) => item.sku === result.sku);
          if (
            saved?.creditBoostPercent === result.creditBoostPercent &&
            saved.creditBoostRevision === result.creditBoostRevision
          ) {
            confirmed.push({
              sku: result.sku,
              creditBoostPercent: result.creditBoostPercent,
              creditBoostRevision: saved.creditBoostRevision,
            });
            nextResults[result.sku] = {
              status: 'confirmed',
              message:
                result.warning === 'above_recommended_limit'
                  ? 'Saved and confirmed (above recommended limit)'
                  : 'Saved and confirmed',
            };
          } else {
            nextResults[result.sku] = { status: 'unconfirmed', message: 'Could not confirm the saved value' };
          }
        }
        if (selectionKeyRef.current !== submittedSelection) {
          return;
        }
        setResults({ ...nextResults });
        if (confirmed.length > 0) {
          onConfirmed(submittedSelection, confirmed);
        }
      } catch {
        if (selectionKeyRef.current === submittedSelection) {
          for (const result of successes) {
            nextResults[result.sku] = { status: 'unconfirmed', message: 'Could not confirm the saved value' };
          }
          setResults({ ...nextResults });
        }
      }
    } catch {
      if (selectionKeyRef.current === submittedSelection) {
        setResults(
          Object.fromEntries(
            updates.map((update) => [
              update.sku,
              { status: 'unconfirmed', message: 'Could not confirm the credit boost update' } satisfies RowResult,
            ]),
          ),
        );
      }
    } finally {
      setIsSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <Card className="mb-3" aria-label="Credit boost editor">
      <Card.Body>
        <Card.Title>Boost Credit (%)</Card.Title>
        {targets.map((target) => {
          const value = parsed.get(target.sku);
          const rowResult = results[target.sku];
          return (
            <Form.Group className="mb-3" controlId={`credit-boost-${target.sku}`} key={target.sku}>
              <Form.Label>Boost Credit (%) for {target.sku}</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={draft[target.sku] ?? ''}
                  disabled={isSaving || isProductSaving}
                  isInvalid={value === null}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, [target.sku]: event.target.value }));
                    setResults((current) => {
                      const next = { ...current };
                      delete next[target.sku];
                      return next;
                    });
                  }}
                />
                <InputGroup.Text>%</InputGroup.Text>
                <Form.Control.Feedback type="invalid">
                  Enter a nonnegative whole number within the safe integer range.
                </Form.Control.Feedback>
              </InputGroup>
              {typeof value === 'number' && value > 10 && (
                <Alert variant="warning" className="mt-2 mb-2">
                  This boost is above the recommended 10% limit.
                </Alert>
              )}
              {typeof value === 'number' && value > 20 && (
                <Form.Check
                  id={`credit-boost-override-${target.sku}`}
                  label={`Approve boost above 20% for ${target.sku}`}
                  checked={overrides[target.sku] ?? false}
                  disabled={isSaving || isProductSaving}
                  onChange={(event) => setOverrides((current) => ({ ...current, [target.sku]: event.target.checked }))}
                />
              )}
              {rowResult && (
                <div
                  role={rowResult.status === 'confirmed' ? 'status' : 'alert'}
                  className={rowResult.status === 'confirmed' ? 'text-success mt-2' : 'text-danger mt-2'}
                >
                  {rowResult.message}
                </div>
              )}
            </Form.Group>
          );
        })}
        <Button type="button" disabled={blocked} onClick={() => void submit()}>
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Saving credit boosts...
            </>
          ) : (
            'Save credit boosts'
          )}
        </Button>
      </Card.Body>
    </Card>
  );
}
